/**
 * The output table for one order: Field | Value | Confidence | Source.
 */

import type { ValidationIssue } from './api';
import {
	EM_DASH,
	type FieldGroup,
	formatValue,
	isEmptyValue,
	NOT_APPLICABLE,
	type Row,
} from './fields';

interface OrderTableProps {
	groups: FieldGroup[];
	/** False when the server supplied no real confidence or source text for these rows. */
	hasConfidence: boolean;
}

type Tone = 'high' | 'mid' | 'low' | 'empty';

/**
 * Colour by confidence — except that `0.0` is not a bad reading.
 *
 * By contract the extractor sets `confidence: 0.0` when the document does not state the
 * value, so a zero marks an absence and gets a neutral grey bar rather than an alarming
 * red one. That covers null values and also the case a real response actually produces:
 * `container.generator_set` comes back as `false` with `confidence: 0.0` when no gen-set
 * was mentioned, and "No" at a red 0 % would read as a misreading rather than a
 * non-mention.
 */
function toneFor(confidence: number, value: unknown): Tone {
	if (confidence === 0 || isEmptyValue(value)) return 'empty';
	if (confidence >= 0.9) return 'high';
	if (confidence >= 0.6) return 'mid';
	return 'low';
}

function severityRank(severity: ValidationIssue['severity']): number {
	if (severity === 'error') return 3;
	if (severity === 'warning') return 2;
	return 1;
}

function worstSeverity(
	issues: ValidationIssue[],
): ValidationIssue['severity'] | null {
	let worst: ValidationIssue['severity'] | null = null;
	for (const issue of issues) {
		if (worst === null || severityRank(issue.severity) > severityRank(worst))
			worst = issue.severity;
	}
	return worst;
}

function IssueList({ issues }: { issues: ValidationIssue[] }) {
	if (issues.length === 0) return null;
	return (
		<ul className="issues">
			{issues.map((issue, index) => (
				<li
					key={`${issue.code}-${index}`}
					className={`issue sev-${issue.severity}`}
				>
					<span className="issue-code">{issue.code}</span>
					<span>{issue.message}</span>
				</li>
			))}
		</ul>
	);
}

function ConfidenceCell({
	row,
	hasConfidence,
}: {
	row: Row;
	hasConfidence: boolean;
}) {
	if (!hasConfidence || row.confidence === null) {
		return <td className="cell-confidence muted">{NOT_APPLICABLE}</td>;
	}

	const confidence = Math.max(0, Math.min(1, row.confidence));
	const tone = toneFor(confidence, row.value);
	const percent = Math.round(confidence * 100);

	return (
		<td className="cell-confidence">
			<div className="conf">
				<span className={`conf-value tone-${tone}`}>{percent}%</span>
				<span
					className="conf-track"
					role="img"
					aria-label={`Confidence ${percent} percent`}
					title={`${row.confidence.toFixed(2)}`}
				>
					<span
						className={`conf-bar tone-${tone}`}
						style={{ width: `${percent}%` }}
					/>
				</span>
			</div>
		</td>
	);
}

function SourceCell({
	row,
	hasConfidence,
}: {
	row: Row;
	hasConfidence: boolean;
}) {
	if (!hasConfidence)
		return <td className="cell-source muted">{NOT_APPLICABLE}</td>;
	if (row.sourceText === null || row.sourceText.trim() === '') {
		return <td className="cell-source muted">{EM_DASH}</td>;
	}
	return (
		<td className="cell-source">
			<span className="snippet-clamp" title={row.sourceText}>
				{row.sourceText}
			</span>
		</td>
	);
}

function FieldRow({
	row,
	hasConfidence,
}: {
	row: Row;
	hasConfidence: boolean;
}) {
	const severity = worstSeverity(row.issues);
	const empty = isEmptyValue(row.value);

	return (
		<tr
			className={severity === null ? undefined : `row-flagged sev-${severity}`}
		>
			<th scope="row" className="cell-label">
				{row.label}
			</th>
			<td className={empty ? 'cell-value muted' : 'cell-value'}>
				<span className="value-text">{formatValue(row.value)}</span>
				{row.note !== null && <span className="row-note">{row.note}</span>}
				<IssueList issues={row.issues} />
			</td>
			<ConfidenceCell row={row} hasConfidence={hasConfidence} />
			<SourceCell row={row} hasConfidence={hasConfidence} />
		</tr>
	);
}

export function OrderTable({ groups, hasConfidence }: OrderTableProps) {
	return (
		<div className="table-wrap">
			<table className="fields">
				<thead>
					<tr>
						<th scope="col" className="col-label">
							Field
						</th>
						<th scope="col" className="col-value">
							Value
						</th>
						<th scope="col" className="col-conf">
							Confidence
						</th>
						<th scope="col" className="col-src">
							Source
						</th>
					</tr>
				</thead>
				{groups.map((group) => (
					<tbody key={group.key}>
						<tr className="group-row">
							<th scope="rowgroup" colSpan={4}>
								<span className="group-title">{group.title}</span>
								{group.subtitle !== null && (
									<span className="group-sub">{group.subtitle}</span>
								)}
							</th>
						</tr>
						{group.warning !== null && (
							<tr className="group-note">
								<td colSpan={4}>
									<p className="warn">{group.warning}</p>
								</td>
							</tr>
						)}
						{group.issues.length > 0 && (
							<tr className="group-note">
								<td colSpan={4}>
									<IssueList issues={group.issues} />
								</td>
							</tr>
						)}
						{group.rows.length === 0 &&
							group.warning === null &&
							group.issues.length === 0 && (
								<tr>
									<td colSpan={4} className="muted">
										No fields
									</td>
								</tr>
							)}
						{group.rows.map((row) => (
							<FieldRow
								key={`${group.key}::${row.path}`}
								row={row}
								hasConfidence={hasConfidence}
							/>
						))}
					</tbody>
				))}
			</table>
		</div>
	);
}
